import { Button, Checkbox, Select, Tooltip } from 'antd';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { StaffEntitySearchContainerEntitySearchFacetsFieldsFragment } from '../../../../generated';
import { Country } from '../../../../types/country';
import FilterOptionLabel from '../../../shared/filter-option-label';

export const EntitySearchFilterParamKey = {
  isIssuingInstitution: 'isIssuingInstitution',
  isClient: 'isClient',
  country: 'country',

  updatedAt: 'updatedAt',
  createdAt: 'createdAt',
  disabledAt: 'disabledAt',

  schemaVersion: 'schemaVersion'
};
const EntitySearchPaginationParamKey = { page: 'page', pageSize: 'pageSize' };

const transformCount = (input: any) => {
  return Object.keys(input)
    .filter((key) => key !== '__typename')
    .reduce((acc: any, key) => {
      const values = input[key]?.reduce((subAcc: any, detail: any) => {
        subAcc[detail.value] = detail.count;
        return subAcc;
      }, {});
      acc[key] = values;
      return acc;
    }, {});
};

const timeFrameFiltersOptions = [
  { idSuffix: 'today', label: 'Today' },
  { idSuffix: 'yesterday', label: 'Yesterday' },
  { idSuffix: 'lastWeek', label: 'Within 7 days' },
  { idSuffix: 'lastMonth', label: 'Within 30 days' },
  { idSuffix: 'lastYear', label: 'Within 365 days' }
];

const timeFrameFilters = [
  {
    label: 'Last Updated',
    category: EntitySearchFilterParamKey.updatedAt,
    items: timeFrameFiltersOptions
  },
  {
    label: 'Created At',
    category: EntitySearchFilterParamKey.createdAt,
    items: timeFrameFiltersOptions
  },
  {
    label: 'Disabled At',
    category: EntitySearchFilterParamKey.disabledAt,
    items: timeFrameFiltersOptions
  }
];

const booleanFilterOptions = [
  { idSuffix: 'true', label: 'Yes' },
  { idSuffix: 'false', label: 'No' }
];

const booleanFilters = [
  {
    label: 'Is Issuing Institution',
    category: EntitySearchFilterParamKey.isIssuingInstitution,
    items: booleanFilterOptions
  },
  {
    label: 'Is Client',
    category: EntitySearchFilterParamKey.isClient,
    items: booleanFilterOptions
  }
];

const schemaVersionFilter = [{ value: '1.0.0', label: '1.0.0' }];

interface EntitySearchFilterProps {
  data: StaffEntitySearchContainerEntitySearchFacetsFieldsFragment;
  setFilters: any;
  countryData: Country[];
}

export interface CheckFilterInterface {
  updatedAt: string[];
  disabledAt: string[];
  createdAt: string[];
  country: string[];
  schemaVersion: string[];
  isClient: boolean | null;
  isClientYesChecked: boolean;
  isClientNoChecked: boolean;
  isIssuingInstitution: boolean | null;
  isIssuingInstitutionYesChecked: boolean;
  isIssuingInstitutionNoChecked: boolean;
}

export const EntitySearchFilter: React.FC<EntitySearchFilterProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const count = transformCount(props?.data);

  const handleFilterCheck = (event: any, filterCategory: string) => {
    searchParams.set(EntitySearchPaginationParamKey.page, '1');

    const { id: filterId, checked } = event.target;

    if (!checked) {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',').filter((item) => item !== filterId);
        if (updatedSearchCategory.length > 0) {
          searchParams.set(filterCategory, updatedSearchCategory.join(','));
        } else {
          searchParams.delete(filterCategory);
        }
        setSearchParams(searchParams);
      }
    } else {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',');
        updatedSearchCategory.push(filterId);
        searchParams.set(filterCategory, updatedSearchCategory.join(','));
        setSearchParams(searchParams);
      } else {
        searchParams.set(filterCategory, filterId);
        setSearchParams(searchParams);
      }
    }
  };

  const isFilterSelected = (idSuffix: string, category: string) => {
    return searchParams.get(category)?.split(',').includes(idSuffix);
  };

  const onClearAllFilters = () => {
    // only do the clear if there are filters on search params
    console.log(searchParams.toString());
    if (
      searchParams
        .toString()
        .split('&')
        .filter((param) => param.split('=')[0] !== 'page' && param.split('=')[0] !== 'pageSize').length === 0
    ) {
      return;
    }

    Object.values(EntitySearchFilterParamKey).forEach((key) => {
      searchParams.delete(key);
    });
    setSearchParams(searchParams);
  };

  const openFilter = (key: string) => {
    return searchParams.get(key) !== null;
  };

  return (
    <div className="flex-[1_1_30%] mr-[10px] min-w-[220px] max-w-[300px] max-h-[200vh] overflow-x-hidden">
      <div style={{marginBottom:'15px'}}>
      <Button type="primary" onClick={onClearAllFilters}>
        Clear All Filters
      </Button>
      </div>

      <div className="overflow-x-hidden overflow-y-auto max-h-[750px] scrollbar-hide">
      {booleanFilters.map((filter) => (
        <FilterOptionLabel key={filter.category} label={filter.label} initiallyOpen={openFilter(filter.category)}>
          {booleanFilterOptions.map((option) => (
            <Checkbox
              key={option.idSuffix}
              id={option.idSuffix}
              checked={isFilterSelected(option.idSuffix, filter.category)}
              onChange={(event: any) => {
                handleFilterCheck(event, filter.category);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[filter.category]?.[option.idSuffix] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>
      ))}

      <FilterOptionLabel label="Country" initiallyOpen={openFilter(EntitySearchFilterParamKey.country)}>
        <Select
          allowClear
          mode="multiple"
          style={{ width: '200px' }}
          placeholder="Select Country"
          value={searchParams.get(EntitySearchFilterParamKey.country)?.split(',')}
          onChange={(value: string[]) => {
            const countries = value.join(',');
            if (countries) {
              searchParams.set(EntitySearchFilterParamKey.country, countries);
            } else {
              searchParams.delete(EntitySearchFilterParamKey.country);
            }
            setSearchParams(searchParams);
          }}
        >
          {props.countryData.map((country: any) => (
            <Select.Option key={country.countryCode} value={country.countryCode}>
              <Tooltip placement='right' title={country.countryName}>{country.countryName}</Tooltip>
            </Select.Option>
          ))}
        </Select>
      </FilterOptionLabel>

      {timeFrameFilters.map((filter) => (
        <FilterOptionLabel key={filter.category} label={filter.label} initiallyOpen={openFilter(filter.category)}>
          {timeFrameFiltersOptions.map((option) => (
            <Checkbox
              key={option.idSuffix}
              id={option.idSuffix}
              checked={isFilterSelected(option.idSuffix, filter.category)}
              onChange={(event: any) => {
                handleFilterCheck(event, filter.category);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[filter.category]?.[option.idSuffix] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>
      ))}

      <FilterOptionLabel label="Schema Version" initiallyOpen={openFilter(EntitySearchFilterParamKey.schemaVersion)}>
        {schemaVersionFilter.map((option) => (
          <Checkbox
            key={option.value}
            id={option.value}
            checked={isFilterSelected(option.value, 'schemaVersion')}
            onChange={(event: any) => {
              handleFilterCheck(event, 'schemaVersion');
            }}
          >
            <p className="checkBoxLabel">
              {option.label} ({count[EntitySearchFilterParamKey.schemaVersion]?.[option.value] ?? 0})
            </p>
          </Checkbox>
        ))}
      </FilterOptionLabel>
    </div>
  </div>
  );
};
